/** Equipment & vehicle report: every item on one page, like a spreadsheet — sort, filter, totals, print, Excel. */
import { useMemo, useState } from 'react';
import { FUNDING, type Funding } from '@civilos/domain';
import { api } from '../shared/api.ts';
import { useRouter } from '../shared/router.tsx';
import { ErrorBox, PageHeader, Spinner, useLoad } from '../shared/ui.tsx';
import { at } from './common.tsx';

type Col = { key: string; label: string; get: (x: any) => string | number | null; num?: boolean; mono?: boolean };
const day = (v: string | null) => (v ? new Date(String(v).length === 10 ? `${v}T12:00:00` : v).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) : '');

const EQ_COLS: Col[] = [
  { key: 'category_label', label: 'Type', get: (x) => x.category_label },
  { key: 'make', label: 'Make', get: (x) => x.make },
  { key: 'model', label: 'Model', get: (x) => x.model },
  { key: 'serial', label: 'Serial #', get: (x) => x.serial, mono: true },
  { key: 'asset_tag', label: 'Asset tag', get: (x) => x.asset_tag, mono: true },
  { key: 'status_label', label: 'Status', get: (x) => x.status_label },
  { key: 'funding', label: 'Funding', get: (x) => (x.funding ? `${FUNDING[x.funding as Funding].short}${x.funding_ref ? ` ${x.funding_ref}` : ''}` : null) },
  { key: 'assigned_name', label: 'Issued to', get: (x) => x.assigned_name },
  { key: 'call_number', label: 'Call #', get: (x) => x.call_number, mono: true },
  { key: 'unit', label: 'Unit / shift', get: (x) => x.unit },
  { key: 'unit_number', label: 'In car', get: (x) => x.unit_number, mono: true },
  { key: 'description', label: 'Description', get: (x) => x.description },
  { key: 'po_number', label: 'PO #', get: (x) => x.po_number, mono: true },
  { key: 'in_service_on', label: 'In service', get: (x) => day(x.in_service_on) },
  { key: 'last_change', label: 'Last change', get: (x) => day(x.last_change) },
  { key: 'last_certified', label: 'Last inventory check', get: (x) => day(x.last_certified) },
  { key: 'notes', label: 'Notes', get: (x) => x.notes },
];
const VEH_COLS: Col[] = [
  { key: 'unit_number', label: 'Unit #', get: (x) => x.unit_number, mono: true },
  { key: 'year', label: 'Year', get: (x) => x.year, num: true },
  { key: 'make', label: 'Make', get: (x) => x.make },
  { key: 'model', label: 'Model', get: (x) => x.model },
  { key: 'color', label: 'Color', get: (x) => x.color },
  { key: 'tag', label: 'Tag', get: (x) => (x.tag ? `${x.tag} ${x.tag_state ?? ''}` : null), mono: true },
  { key: 'vin', label: 'VIN', get: (x) => x.vin, mono: true },
  { key: 'status_label', label: 'Status', get: (x) => x.status_label },
  { key: 'funding', label: 'Funding', get: (x) => (x.funding ? `${FUNDING[x.funding as Funding].short}${x.funding_ref ? ` ${x.funding_ref}` : ''}` : null) },
  { key: 'assigned_name', label: 'Assigned to', get: (x) => x.assigned_name },
  { key: 'call_number', label: 'Call #', get: (x) => x.call_number, mono: true },
  { key: 'mileage', label: 'Mileage', get: (x) => x.mileage, num: true },
  { key: 'mileage_at', label: 'Entered', get: (x) => day(x.mileage_at) },
  { key: 'oil_left', label: 'Oil change in', get: (x) => x.oil_left, num: true },
  { key: 'equipment', label: 'Equipment', get: (x) => x.equipment, num: true },
  { key: 'rfid_tag', label: 'RFID', get: (x) => x.rfid_tag, mono: true },
  { key: 'fuel_card', label: 'Fuel card', get: (x) => x.fuel_card, mono: true },
    { key: 'in_service_on', label: 'In service', get: (x) => day(x.in_service_on) },
];

function Sheet({ rows, cols, open }: { rows: any[]; cols: Col[]; open: (x: any) => void }) {
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const shown = cols.filter((c) => !hidden.has(c.key));
  const sorted = useMemo(() => {
    if (!sort) return rows;
    const c = cols.find((x) => x.key === sort.key)!;
    return [...rows].sort((a, b) => {
      const va = c.get(a);
      const vb = c.get(b);
      if (va == null || va === '') return 1;
      if (vb == null || vb === '') return -1;
      return (typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb), undefined, { numeric: true })) * sort.dir;
    });
  }, [rows, sort, cols]);
  return (
    <>
      <details className="ar-cols no-print">
        <summary className="small">Columns ({shown.length} of {cols.length})</summary>
        <div className="bwc-checks">
          {cols.map((c) => (
            <label key={c.key} className="check">
              <input type="checkbox" checked={!hidden.has(c.key)} onChange={() => setHidden((h) => (h.has(c.key) ? new Set([...h].filter((k) => k !== c.key)) : new Set([...h, c.key])))} /> {c.label}
            </label>
          ))}
        </div>
      </details>
      <div className="ar-wrap">
        <table className="ar-sheet">
          <thead>
            <tr>
              <th className="ar-n">#</th>
              {shown.map((c) => (
                <th key={c.key} aria-sort={sort?.key === c.key ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'}>
                  <button type="button" onClick={() => setSort(sort?.key === c.key ? (sort.dir === 1 ? { key: c.key, dir: -1 } : null) : { key: c.key, dir: 1 })}>
                    {c.label}
                    {sort?.key === c.key ? (sort.dir === 1 ? ' ▲' : ' ▼') : ''}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((x, i) => (
              <tr key={x.id} onClick={() => open(x)}>
                <td className="ar-n">{i + 1}</td>
                {shown.map((c) => {
                  const v = c.get(x);
                  return (
                    <td key={c.key} className={`${c.mono ? 'mono' : ''}${c.num ? ' num' : ''}`}>
                      {typeof v === 'number' ? v.toLocaleString() : v ?? ''}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export function AssetReportPage() {
  const { navigate } = useRouter();
  const qs = new URLSearchParams(location.search);
  const [tab, setTab] = useState<'equipment' | 'vehicles'>(qs.get('tab') === 'vehicles' ? 'vehicles' : 'equipment');
  const [category, setCategory] = useState(qs.get('category') ?? '');
  const [status, setStatus] = useState(qs.get('status') ?? '');
  const [q, setQ] = useState('');
  const query = `category=${encodeURIComponent(category)}&status=${encodeURIComponent(status)}&q=${encodeURIComponent(q)}`;
  const eq = useLoad(() => (tab === 'equipment' ? api.get<any>(`/api/equipment/report?${query}`) : Promise.resolve(null)), [tab, category, status, q]);
  const vh = useLoad(() => (tab === 'vehicles' ? api.get<any>(`/api/fleet/report?status=${encodeURIComponent(status)}&q=${encodeURIComponent(q)}`) : Promise.resolve(null)), [tab, status, q]);
  const data = tab === 'equipment' ? eq : vh;
  const statuses: Record<string, string> = data.data?.statuses ?? {};
  const rows = tab === 'equipment' ? eq.data?.items ?? [] : vh.data?.vehicles ?? [];
  const catLabel = category && eq.data ? eq.data.categories.find((c: any) => c.code === category)?.label : null;
  const title = tab === 'equipment' ? `${catLabel ?? 'All equipment'}${status && status !== 'all' ? ` — ${statuses[status] ?? status}` : ''}` : `Vehicles${status && status !== 'all' ? ` — ${statuses[status] ?? status}` : ''}`;
  return (
    <div className="ops stack ar-page">
      <PageHeader
        crumb="Fleet & equipment"
        title="Equipment report"
        sub="Every item on one page. Click a column to sort; print it or open it in Excel."
        actions={
          <div className="row gap-sm no-print">
            <button className="btn" onClick={() => window.print()}>
              Print
            </button>
            <a className="btn primary" href={tab === 'equipment' ? `/api/equipment/report?${query}&format=csv` : `/api/fleet/report?status=${encodeURIComponent(status)}&q=${encodeURIComponent(q)}&format=csv`}>
              Open in Excel
            </a>
          </div>
        }
      />
      <div className="row gap-sm wrap no-print">
        <div className="seg-ctl" role="tablist" aria-label="Report">
          <button type="button" role="tab" aria-selected={tab === 'equipment'} className={tab === 'equipment' ? 'on' : ''} onClick={() => (setTab('equipment'), setStatus(''))}>
            Equipment
          </button>
          <button type="button" role="tab" aria-selected={tab === 'vehicles'} className={tab === 'vehicles' ? 'on' : ''} onClick={() => (setTab('vehicles'), setStatus(''))}>
            Vehicles
          </button>
        </div>
        {tab === 'equipment' && (
          <select value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Type" style={{ maxWidth: 240 }}>
            <option value="">All types</option>
            {(eq.data?.categories ?? []).map((c: any) => (
              <option key={c.code} value={c.code}>
                {c.label}
              </option>
            ))}
          </select>
        )}
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status" style={{ maxWidth: 220 }}>
          <option value="">All but retired</option>
          {Object.entries(statuses).map(([k, l]) => (
            <option key={k} value={k}>
              {l}
            </option>
          ))}
          <option value="all">Everything (with retired)</option>
        </select>
        <input type="search" placeholder="Serial, tag, person, call #, car…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search the report" style={{ maxWidth: 260 }} />
      </div>
      <h2 className="ar-title">
        {title} · {rows.length} {rows.length === 1 ? 'item' : 'items'}
        <span className="small muted"> · {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
      </h2>
      {tab === 'equipment' && eq.data && eq.data.totals.length > 0 && (
        <div className="ar-totals">
          {eq.data.totals.map((t: any) => (
            <button key={t.category} type="button" className={`ar-total${category === t.category ? ' on' : ''}`} onClick={() => setCategory(category === t.category ? '' : t.category)}>
              <strong>{t.total}</strong> {t.label}
              <span className="small muted"> · {Object.entries(t.counts).map(([s, n]) => `${n} ${(statuses[s] ?? s).toLowerCase()}`).join(', ')}</span>
            </button>
          ))}
        </div>
      )}
      {tab === 'vehicles' && vh.data && (
        <div className="ar-totals">
          {Object.entries(vh.data.counts).map(([s, n]) => (
            <button key={s} type="button" className={`ar-total${status === s ? ' on' : ''}`} onClick={() => setStatus(status === s ? '' : s)}>
              <strong>{n as number}</strong> {statuses[s] ?? s}
            </button>
          ))}
        </div>
      )}
      {data.error ? (
        <ErrorBox error={data.error} />
      ) : !data.data ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <p className="muted">Nothing matches.</p>
      ) : (
        <Sheet key={tab} rows={rows} cols={tab === 'equipment' ? EQ_COLS : VEH_COLS} open={(x) => navigate(at(tab === 'equipment' ? `/equipment/${x.id}` : `/fleet/${x.id}`))} />
      )}
    </div>
  );
}
